import { IconButton } from '@/ui/common/Buttons/IconButton';
import { DashedDivider } from '@/ui/common/Dividers/DashedDivider';
import { FormDivider } from '@/ui/common/Dividers/FormDivider';
import { CloseIcon } from '@/ui/common/Icons/CloseIcon';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';
import { URLs } from '@/service/constants/urls';
import Link from 'next/link';

type TCartItemProps = {
    title: string;
    bookedAt: string;
    returnAt: string;
    productId: string;
    tickets: { booked: string; name: string; price: string }[];
    options: { name: string; value: { name: string; price: string } }[];
    comment: string;
    onRemoveCartItem: () => void;
    subtotal: number;
    activityLink: string;
    discountRate?: number;
};

export const CartItem = ({
    title,
    bookedAt,
    returnAt,
    productId,
    tickets,
    options,
    comment,
    onRemoveCartItem,
    subtotal,
    activityLink,
    discountRate,
}: TCartItemProps) => (
    <Link
        href={activityLink}
        className="relative w-full rounded-xl bg-ash dark:bg-blue-1 md:rounded-2xl"
        data-test-id="cart-item"
    >
        <IconButton
            onClick={(e) => {
                e.preventDefault();
                onRemoveCartItem();
            }}
            icon={
                <CloseIcon
                    className={
                        'h-[30px] w-[30px] min-w-[30px] md:h-[36px] md:w-[36px] md:min-w-[36px]'
                    }
                />
            }
            className="absolute right-2 top-2 h-4 w-4 rounded-full md:right-3 md:top-3 md:h-5 md:w-5"
            data-test-id="close-btn"
        />
        <div className="flex">
            <div className="w-full px-5 py-4 pr-0 md:px-10 md:py-8 md:pr-0 lg:px-11 lg:py-10 lg:pr-0">
                <p
                    className="tx-large-medium text-gray-6 dark:text-white"
                    data-test-id="cart-item-title"
                >
                    {title}
                </p>
                <p
                    className="tx-large-regular flex flex-wrap text-gray-12 dark:text-gray-3"
                    data-test-id="item-booked-at"
                >
                    {!!returnAt && <p className="tx-large-semibold mr-2">Departure: </p>}
                    {bookedAt}
                </p>
                {!!returnAt && (
                    <p
                        className="tx-large-regular flex flex-wrap text-gray-12 dark:text-gray-3"
                        data-test-id="item-return-at"
                    >
                        <p className="tx-large-semibold mr-2">Return: </p>
                        {returnAt}
                    </p>
                )}
            </div>
            <picture className="m-2 mr-9 h-full min-w-[100px] overflow-hidden rounded-2xl shadow-lightCard dark:shadow-darkCard md:m-4 md:mr-12 md:min-w-[130px]  lg:m-5 lg:mr-12 lg:min-w-[144px]">
                <source
                    srcSet={`${URLs.image}/activity/${productId}/center/100x58/wr.jpg`}
                    media="(max-width: 500px)"
                    type="image/webp"
                />
                <source
                    srcSet={`${URLs.image}/activity/${productId}/center/100x58/wr.jpg`}
                    media="(max-width: 500px)"
                    type="image/jpeg"
                />
                <source
                    srcSet={`${URLs.image}/activity/${productId}/center/130x76/wr.jpg`}
                    media="(max-width: 767px)"
                    type="image/webp"
                />
                <source
                    srcSet={`${URLs.image}/activity/${productId}/center/130x76/wr.jpg`}
                    media="(max-width: 767px)"
                    type="image/jpeg"
                />
                <source
                    srcSet={`${URLs.image}/activity/${productId}/center/144x84/wr.jpg`}
                    media="(min-width: 767px)"
                    type="image/webp"
                />
                <source
                    srcSet={`${URLs.image}/activity/${productId}/center/144x84/wr.jpg`}
                    media="(min-width: 767px)"
                    type="image/jpeg"
                />
                <img
                    src={`${URLs.image}/activity/${productId}/center/100x58/wr.jpg`}
                    alt="cart-item"
                    className="h-full w-full object-cover"
                />
            </picture>
        </div>

        <DashedDivider withCircle />

        <div className="w-full px-5 py-4 md:px-10 md:py-8 lg:px-11 lg:py-10">
            {tickets.map(({ booked, name, price }, idx) => (
                <div
                    key={`ticket-${idx}`}
                    className="flex w-full items-center justify-between"
                    data-test-id="item-ticket"
                >
                    <div className="flex items-start gap-x-7">
                        <img src="/icon/Ticket.svg" alt="Ticket" />
                        <span className="tx-large-medium text-gray-6 dark:text-white">
                            {booked} x {name}
                        </span>
                    </div>
                    <span className="tx-large-medium text-gray-6 dark:text-white">
                        {getFormattedPrice(price)}
                    </span>
                </div>
            ))}

            {!!options.length && (
                <>
                    {options.map(({ name, value }, idx) => (
                        <div key={`options-${idx}`} className={'my-1'} data-test-id="item-option">
                            <FormDivider className="mb-3 mt-4" />
                            <div className="flex w-full items-start justify-between">
                                <div className="flex items-start gap-x-7">
                                    <img
                                        src="/icon/Plus.svg"
                                        alt="Protection"
                                        className=" h-6 w-6"
                                    />
                                    <div className={'mr-3'}>
                                        <span
                                            className={
                                                'tx-large-medium mr-2 text-gray-6 dark:text-white'
                                            }
                                        >
                                            {name}:
                                        </span>
                                        <span className="tx-large-regular text-gray-12 dark:text-gray-3">
                                            {value.name}
                                        </span>
                                    </div>
                                </div>
                                <span className="tx-large-medium text-gray-6 dark:text-white">
                                    {getFormattedPrice(value.price, 'USD')}
                                </span>
                            </div>
                        </div>
                    ))}
                </>
            )}

            {!!comment && (
                <>
                    <FormDivider className="mb-3 mt-4" />
                    <div className="flex w-full items-start gap-x-7" data-test-id="item-comment">
                        <img src="/icon/Change.svg" alt="Change" />
                        <div>
                            <p className="tx-large-medium text-gray-6 dark:text-white">Comment</p>
                            <blockquote className="tx-large-regular text-gray-12 dark:text-gray-3">
                                {comment}
                            </blockquote>
                        </div>
                    </div>
                </>
            )}

            {!!discountRate && (
                <>
                    <FormDivider className="mb-3 mt-4" />
                    <div className={'my-1'} data-test-id="discount-rate">
                        <div className="flex w-full items-start justify-between text-brand">
                            <div className="flex items-start gap-x-7">
                                <img src="/icon/Protection.svg" alt="Protection" />
                                <div className={'mr-3'}>
                                    <span className={'tx-large-medium mr-2'}>Discount Applied</span>
                                </div>
                            </div>
                            <span className="tx-large-medium mt-1">
                                -{getFormattedPrice(discountRate, 'USD')}
                            </span>
                        </div>
                    </div>
                </>
            )}

            <FormDivider className="mb-3 mt-4" />
            <div className="flex items-center justify-end">
                <span className="tx-large-regular mr-3 text-gray-12 dark:text-gray-3">
                    Subtotal:
                </span>

                <span
                    className="tx-large-medium text-gray-6 dark:text-white"
                    data-test-id="cart-item-subtotal"
                >
                    {getFormattedPrice(subtotal)}
                </span>
            </div>
        </div>
    </Link>
);
